export type ValidationType =
  | "dni"
  | "nie"
  | "alphanumeric"
  | "nif"
  | "cif"
  | "email"
  | "alpha"
  | "numeric"
  | "cadastral";

export const useValidator = (type?: ValidationType) => {

  const ALPHANUMERIC_REGEXP = /^[a-zA-Z0-9]+$/i;
  const ALPHA_REGEXP = /^[a-zA-Z]+$/i;
  const NUMERIC_REGEXP = /^[0-9]+$/i;
  const NIE_REGEXP = /^[XYZ][0-9]{7}[A-Z]$/i;
  const CIF_REGEXP = /^[A-W][0-9]{7}[A-Z]$/i;
  const DNI_REGEXP = /^[0-9]{8}[A-Z]$/i;
  const EMAIL_REGEXP =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  const controlCharacterIsValid = (value: string) => {
    const letters = [
      "T",
      "R",
      "W",
      "A",
      "G",
      "M",
      "Y",
      "F",
      "P",
      "D",
      "X",
      "B",
      "N",
      "J",
      "Z",
      "S",
      "Q",
      "V",
      "H",
      "L",
      "C",
      "K",
      "E",
      "T",
    ];
    const newValue = value.substring(0, value.length - 1);
    const letter = value.substring(value.length - 1, value.length);
    const index = +newValue % 23;
    return letters[index] === letter;
  };

  const cifControlCharacterIsValid = (value: string) => {
    const letters = ["0", "A", "B", "C", "D", "E", "F", "G", "H", "I", "J"];
    const numericValue = value.substring(1, value.length - 1);
    const controlDigit = value.substring(value.length - 1, value.length);
    const partA = +numericValue[1] + +numericValue[3] + +numericValue[5];
    const partB =
      +computeCifControlElement(numericValue, 0) +
      +computeCifControlElement(numericValue, 2) +
      +computeCifControlElement(numericValue, 4) +
      +computeCifControlElement(numericValue, 6);
    const finalResult = partA + partB;
    const index = 10 - +`${finalResult}`.charAt(`${finalResult}`.length - 1);
    return letters[index] === controlDigit;
  };

  const computeCifControlElement = (value: string, index: number) => {
    const numericValue = +value.charAt(index) * 2;
    const stringValue = numericValue.toString();
    const tens = +stringValue.charAt(0) ?? 0;
    const units = +stringValue.charAt(1) ?? 0;
    return tens + units;
  };

  const cadastralReferenceDigit = (value: string) => {
    const positionWeight = [13, 15, 12, 5, 4, 17, 9, 21, 3, 7, 1];
    const controlCharacters = ["M","Q","W","E","R","T","Y","U","I","O","P","A","S","D","F","G","H","J","K","L","B","Z","X"];
    let digitSum = 0
    
    value.toUpperCase().split("").forEach((character, index) => {
      let numericValue = 0
      if (character >= "A" && character <= "N") {
        numericValue = character.charCodeAt(0) - 64;
      } else if (character === "Ñ") {
        numericValue = 15;
      } else if (character > "N") {
        numericValue = character.charCodeAt(0) - 63;
      }
      digitSum = (digitSum + numericValue * positionWeight[index]) % 23;
    })
    return controlCharacters[digitSum]
  }

  const cadastralReference = (value: string) => {
    if(value.length === 14) {
      return alphanumeric(value)
    } else if(value.length === 18) {
      return alphanumeric(value)
    } else if (value.length === 20) {
      const firstCD = (
        value.substring(0, 7) +
        value.substring(14, 18)
      ).toUpperCase();
      const secondCD = (
        value.substring(7, 14) +
        value.substring(14, 18)
      ).toUpperCase();
      const calculation = cadastralReferenceDigit(firstCD) + cadastralReferenceDigit(secondCD);
      return calculation === value.toUpperCase().substring(18, 20)
    }
    return false
  };

  const nie = (value: string) => {
    const regex = new RegExp(NIE_REGEXP, "g");
    return (
      value.match(regex) != null &&
      controlCharacterIsValid(value.substring(1, value.length))
    );
  };

  const dni = (value: string) => {
    const regex = new RegExp(DNI_REGEXP, "g");
    return value.match(regex) != null && controlCharacterIsValid(value);
  };

  const cif = (value: string) => {
    const regex = new RegExp(CIF_REGEXP, "g");
    return value.match(regex) != null && cifControlCharacterIsValid(value);
  };

  const nif = (value: string) => {
    return dni(value) || nie(value);
  };

  const alphanumeric = (value: string) => {
    const regex = new RegExp(ALPHANUMERIC_REGEXP, "g");
    return value.match(regex) != null;
  };

  const alpha = (value: string) => {
    const regex = new RegExp(ALPHA_REGEXP, "g");
    return value.match(regex) != null;
  };

  const numeric = (value: string) => {
    const regex = new RegExp(NUMERIC_REGEXP, "g");
    return value.match(regex) != null;
  };

  const email = (value: string) => {
    const regex = new RegExp(EMAIL_REGEXP, "g");
    return value.match(regex) != null;
  };

  const noValidation = (value: string) => true;

  const validationFor = (type?: ValidationType) => {
    switch (type) {
      case "email":
        return email;
      case "dni":
        return dni;
      case "nie":
        return nie;
      case "nif":
        return nif;
      case "cif":
        return cif;
      case "alpha":
        return alpha;
      case "alphanumeric":
        return alphanumeric;
      case "numeric":
        return numeric;
      case "cadastral":
        return cadastralReference;
      default:
        return noValidation;
    }
  };

  const isValid = validationFor(type);

  return {
    isValid,
    dni,
    nie,
    nif,
    cif,
    alphanumeric,
    alpha,
    cadastralReference,
    email,
    numeric
  };
};
